import React from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import { motion } from "framer-motion";
import { SparkLineChart } from "@mui/x-charts/SparkLineChart";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import { KPI } from "../../Dashboard/types/dashboardTypes";
import { dashboardKpiTitle } from "../../Dashboard/utils/dashboardI18n";
import { FC } from "react";

interface MetricCardProps {
  kpiMetric: KPI;
  period: string;
  index?: number;
}

const MetricCard: FC<MetricCardProps> = ({ kpiMetric, period, index = 0 }) => {
  const { t } = useKivunovaTranslation();
  const { title, value, trend, spark, positive, valueColor, valueFull } =
    kpiMetric;
  const displayTitle = dashboardKpiTitle(kpiMetric.id, title, t);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.4,
        delay: index * 0.05,
        ease: [0.25, 0.46, 0.45, 0.94]
      }}
      whileHover={{ y: -2 }}
      style={{ height: "100%" }}
    >
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          p: 2,
          borderRadius: 2.5,
          minHeight: 112,
          boxShadow: "0 2px 8px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)",
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
          transition:
            "box-shadow 0.25s ease, border-color 0.2s ease, transform 0.2s ease",
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 3,
            bgcolor: "primary.main",
            opacity: 0.2
          },
          "&:hover": {
            boxShadow:
              "0 8px 24px rgba(0,0,0,0.06), 0 2px 6px rgba(0,0,0,0.04)",
            borderColor: "action.hover",
            "&::before": { opacity: 0.4 }
          }
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={500}
          sx={{ mb: 1, letterSpacing: "0.01em", pl: 0.5 }}
        >
          {displayTitle}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            flex: 1,
            justifyContent: "center"
          }}
        >
          {value === "—" || !value?.trim() ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.75rem" }}
            >
              {t("dashboard-metric-no-data", "No data")}
            </Typography>
          ) : (
            <Tooltip
              title={valueFull ?? ""}
              disableHoverListener={!valueFull}
              placement="top"
              arrow
            >
              <Typography
                variant="h3"
                fontWeight={700}
                component="span"
                sx={{
                  fontSize: { xs: "1.35rem", sm: "1.5rem" },
                  lineHeight: 1.2,
                  cursor: valueFull ? "help" : undefined,
                  ...(valueColor && { color: valueColor })
                }}
              >
                {value}
              </Typography>
            </Tooltip>
          )}
          {spark && spark.length > 1 && (
            <Box sx={{ width: 64, height: 40, mt: 1 }}>
              <SparkLineChart
                data={spark}
                color={positive ? "#2e7d32" : "#c62828"}
                area
                curve="catmullRom"
                showTooltip
                opacity={0.2}
              />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "baseline",
            gap: 0.25
          }}
        >
          <Typography
            variant="caption"
            component="span"
            color={positive ? "success.main" : "error.main"}
            sx={{ fontWeight: 600, fontSize: "0.7rem" }}
          >
            {positive ? "↑" : "↓"} {trend}%
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.7rem" }}
          >
            {t("dashboard-metric-vs-word", "vs")} {period}
          </Typography>
        </Box>
      </Box>
    </motion.div>
  );
};

export default MetricCard;
